import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import useAuthentication from 'utils/useAuthentication';
import { Button, InfoDialog, BodyProgress, Header, FormueberegnerHeader, Footer, CookieConsent, Maintenance, NotificationBanner, AffiliateTermsDialog, Loader } from 'components';
import { fetchKreditdata } from 'store/actions/kreditdata';
import getConfig from 'next/config';
import { getOrders } from 'api/orders';
import { Routes } from 'utils/routes';
import useAffiliateId from 'hooks/useAffiliateId';
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';

// Dynamically import the chatbot script
const ChatbotScript = dynamic(() => import('utils/chatbotScript'), { ssr: false });

const Layout = ({ children }) => {
    const dispatch = useDispatch();
    const { authenticated } = useAuthentication();
    const { publicRuntimeConfig } = getConfig();
    const router = useRouter();
    const [fetching, setFetching] = useState(false);
    const [showUnfinishedOrderDialog, setShowUnfinishedOrderDialog] = useState(true);

    const closeUnfinisedOrderDialog = () => {
        setShowUnfinishedOrderDialog(false);
    };

    // we generate a canonical URL for SEO, if possible
    // if generated, it is attached on each page
    let canonicalUrl;
    if (publicRuntimeConfig.baseUrl) canonicalUrl = `${publicRuntimeConfig.baseUrl}${router.route}`;

    const googleTagManagerId = publicRuntimeConfig.googleTagManager?.id;
    const googleAnalyticsId = publicRuntimeConfig.googleAnalytics?.id;
    const showSharedHeader = router.pathname !== '/formueberegner';

    // move to kreditdata interceptor
    const isServer = typeof window === 'undefined';
    useEffect(() => {
        if (!isServer && authenticated && router.query.kreditdata_state && !router.query.userId && !fetching ) {
            setFetching(true);
            // timeout for auth fix, to make sure we have aquired token once we do kreditdata-data-fetch
            setTimeout(() => dispatch(fetchKreditdata(router.query.kreditdata_id)), 1000);
            router.push(window.location.pathname);
        }
    }, [authenticated, isServer, fetching, dispatch, router]);

    const [orders, setOrders] = useState();
    const [unfinishedOrder, setUnfinishedOrder] = useState(null);

    useAffiliateId();

    useEffect(() => {
        const run = async () => {
            const result = await getOrders();
            setOrders(result);
        };
        if(authenticated && !orders){
            run();
        }

    }, [orders, authenticated]);
    useEffect(() => {
        const run = async () => {
            const unfinishedOrder = orders.find(x => !!x.dataCollectionProgress?.numberOfSteps && !x.dataCollectionProgress.completed && x.status!='cancelled' && !!x.items[0].product);
            setUnfinishedOrder(unfinishedOrder);
            const orderRoute = Routes.MyPenly.Orders.Order(unfinishedOrder?.orderId);
            if(unfinishedOrder && router.pathname === '/'){
                router.push(`${orderRoute}?continueOrder=true`);
            }
        };
        if(orders){
            run();
        }

    }, [orders]);
    const isInDataCollectionFlow = router.pathname.includes('ordrer') || router.pathname.includes('kvittering')|| router.pathname.includes('deling');
    const affiliate = Object.values(publicRuntimeConfig.affiliateNetworks).find(affiliateNetwork => router.query[affiliateNetwork.parameterName]);
    return (
        <>
            { publicRuntimeConfig.googleTagManager?.enabled && (
                <noscript>
                    <iframe
                        src={ `https://tagmanager.deranged.dk/ns.html?id=${googleTagManagerId}` }
                        height="0"
                        width="0"
                        style={{ display: 'none', visibility: 'hidden' }}
                    >
                    </iframe>
                </noscript>
            ) }

            { fetching && <Loader/> }
            <BodyProgress />

            { publicRuntimeConfig.notificationBanner?.enabled && (
                <NotificationBanner message={ publicRuntimeConfig.notificationBanner?.message } />
            ) }



            <div id="body-container">
                <Script src="/static/js/mtm.js" type="text/javascript" />
                <Head>
                    <meta charSet="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap" />
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Raleway:wght@700;900&display=swap" />
                    <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
                    <link rel="icon" href="/favicon.ico" />
                    <link rel="icon" href="/static/gfx/fav/icon.svg" type="image/svg+xml" />
                    <link rel="apple-touch-icon" href="/static/gfx/fav/apple-touch-icon.png" />
                    <link rel="manifest" href="/site.webmanifest" />
                    <meta name="theme-color" content="#ffffff" />
                    { canonicalUrl && <link rel="canonical" href={ canonicalUrl } key="canonical" /> }

                    { publicRuntimeConfig.googleAnalytics?.enabled && (
                        <>
                            <script async src={ `https://tagmanager.deranged.dk/gtag/js?id=${googleAnalyticsId}` }></script>
                            <Script>
                                {
                                    `
                                        window.dataLayer = window.dataLayer || [];
                                        function gtag(){dataLayer.push(arguments);}
                                        gtag('js', new Date());
                                        gtag('config', '${googleAnalyticsId}', { transport_url: 'ga-proxy.deranged.dk' });
                                    `
                                }
                            </Script>
                        </>
                    ) }
                </Head>
                { publicRuntimeConfig.maintenance.enabled ? (
                    <>
                        <Maintenance title={ publicRuntimeConfig.maintenance.title } message={ publicRuntimeConfig.maintenance.message } date={ publicRuntimeConfig.maintenance.date } />
                    </>
                ) : (
                    <>
                        { showSharedHeader ? (
                            <Header focused={ router.pathname === '/raadgivning/nedsparingsraadgivning-webinar' } />
                        ) : (
                            <FormueberegnerHeader />
                        ) }
                        { unfinishedOrder  &&  !isInDataCollectionFlow && !fetching && !router.query.continueOrder && showUnfinishedOrderDialog && (
                            <div style={{ top: 0, zIndex: 20, position: 'relative', display:'flex', justifyContent:'center', width:'100%' }}>
                                <motion.div animate={{ y: 0, opacity: 1 }} initial={{ y: '-200px', opacity: 0 }} transition={{ type: 'spring', stiffness: 100 }}>
                                    <InfoDialog
                                        marker="!"
                                        color="highlight"
                                        onClose={ closeUnfinisedOrderDialog }
                                        initialContent={
                                            <>
                                                <h4>Uafsluttet ordre</h4>
                                                <p>Din rådgiver har brug for dine data for at kunne give dig den bedste rådgivning. </p>
                                                <div className="buttonGroup">
                                                    <Button color="highlight" size="small" onClick={ () =>router.push(`${Routes.MyPenly.Orders.Order(unfinishedOrder.orderId)}?continueOrder=true`) }>
                                                        Klik her for at færdiggøre din ordre
                                                    </Button>

                                                    <a onClick={ closeUnfinisedOrderDialog }>Ikke lige nu</a>
                                                </div>
                                            </>
                                        }
                                    />
                                </motion.div>

                            </div>
                        ) }
                        <main>{ children }</main>
                        <Footer />
                        { (publicRuntimeConfig.chatbot?.enabled || router.query.chatbottest !== undefined) &&
                        <ChatbotScript />
                        }
                        { publicRuntimeConfig.supportChatActive && (
                            <Script strategy="lazyOnload">
                                {
                                    'var FreeScoutW={s:{"color":"#00a3bd","position":"br","locale":"da","id":4152464896}};(function(d,e,s){if(d.getElementById("freescout-w"))return;a=d.createElement(e);m=d.getElementsByTagName(e)[0];a.async=1;a.id="freescout-w";a.src=s;m.parentNode.insertBefore(a, m)})(document,"script","https://admin.support.penly.dk/modules/chat/js/widget.js?v=3715");'
                                }
                            </Script>
                        ) }
                        { publicRuntimeConfig.poptin?.enabled && (
                            <>
                                <script>var poptin_single_page_app = true;</script>
                                <Script id='pixel-script-poptin' src='https://cdn.popt.in/pixel.js?id=ca63c7b97c2e2' async='true'></Script>
                            </>
                        ) }
                        { publicRuntimeConfig.googleTagManager?.enabled && (
                            <>
                                <Script strategy="lazyOnload">
                                    {
                                        `
                                            (function() {
                                                var w = window;
                                                var d = document;
                                                w['dataLayer'] = w['dataLayer'] || [];
                                                w['dataLayer'].push({ 'gtm.start': new Date().getTime(), event:'gtm.js' });

                                                var f = d.getElementsByTagName('script')[0]
                                                var j = d.createElement('script');

                                                j.async = true;
                                                j.src = 'https://tagmanager.deranged.dk/gtm.js?id=${googleTagManagerId}';
                                                f.parentNode.insertBefore(j, f);
                                            })();
                                        `
                                    }
                                </Script>
                            </>
                        ) }
                    </>
                ) }
            </div>
            { affiliate ? <AffiliateTermsDialog affiliate={ affiliate } /> : <CookieConsent /> }
        </>
    );
};

export default Layout;
